/**
 * List editor (CC_05 Block 8, D-18). A list is `files.kind='list'`; this is its
 * own full-screen page (board pattern) with the only representation v1 ships:
 * the table view. It reuses the shared sort (Block 4) and filter (Block 5).
 * Canvas embedding of a list is v1.4 (Ü-19), not here.
 *
 * Cells edit inline and autosave per row. Relation columns show their link count
 * (row-mode) or a link to the referenced board (board-mode, e.g. auto-boards);
 * full relation editing over the UI is deferred — the data model, routes and MCP
 * carry it (the DoD is proven there).
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { ryfeApi } from "../api";
import {
  formatNumber,
  normalizeColor,
  urlLabelFor,
  urlTextCell,
  urlTextLabel,
  urlView,
  type NumberFormat,
  type UrlFormat,
} from "../list-format";
import {
  downloadBytes,
  downloadText,
  listToPdfLines,
  decodeCsvBuffer,
  detectDelimiter,
  parseCSV,
  safeFilename,
  textToPdf,
  toCSV,
} from "../file-export";
import { matrixToXlsx, xlsxToMatrix } from "../xlsx";
import { backToFiles, navigate } from "../router";
import { useRyfe } from "../RyfeContext";
import { useDocumentTitle } from "../use-doc-title";
import { createEncryptedSnapshot } from "../share";
import { sortRows, useTableSort } from "../table-sort";
import { applyFilter, EMPTY_FILTER, type FilterConfig } from "../table-filter";

import { FilterBar } from "./FilterBar";
import { ColumnDialog, type ColumnDraft } from "./ColumnDialog";
import { RRuleSelector } from "./RRuleSelector";
import { ProcessInstantiateDialog } from "./ProcessInstantiateDialog";
import { FormsDialog } from "./FormsDialog";
import { RelationPicker } from "./RelationPicker";
import { RowComments } from "./RowComments";
import { ContactAddresses } from "./ContactAddresses";
import { ShareSubmenu } from "./ShareSubmenu";

import "./ryfe.scss";

import type {
  ApiError,
  ListColumn,
  ListColumnType,
  ListRow,
  ListSnapshot,
} from "../api";

// A3b (D-158a/D-160) — der CRM-Reiter-Container: EINE CRM-Erfahrung mit drei
// Reitern (Tabs) im Listen-Editor-Kopf, umgeschaltet über den entity_type-
// Adapter (contacts/companies/persons). Agenda + Kontakt-Typen sind eigene
// Systemlisten und NICHT Teil des Reiter-Containers. Reihenfolge = kanonisch.
const CRM_TAB_TYPES: { listType: string; label: string }[] = [
  { listType: "crm.kontakte", label: "Kontakte" },
  { listType: "crm.firmen", label: "Firmen" },
  { listType: "crm.personen", label: "Personen" },
];
const isCrmTabType = (t: string | null): boolean =>
  !!t && CRM_TAB_TYPES.some((x) => x.listType === t);

const TYPE_LABELS: Record<ListColumnType, string> = {
  text: "Text",
  number: "Zahl",
  checkbox: "Checkbox",
  date: "Datum",
  select: "Auswahl",
  url: "URL",
  url_text: "URL + Text",
  color: "Farbe",
  webhooks: "Webhooks",
  uuid: "UUID",
  relation: "Relation",
};

// CC_07 Block 1 (D-40) — a normalised select option. `config.options` may be a
// list of plain strings or of `{ value, label, color }` objects (Mockup v3 §C);
// both collapse to this shape. An option with no value is dropped.
export interface SelectOption {
  value: string;
  label: string;
  color?: string;
}

export const readSelectOptions = (
  config: Record<string, unknown> | undefined,
): SelectOption[] => {
  const raw = config?.options;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((o): SelectOption => {
      if (typeof o === "string") {
        return { value: o, label: o };
      }
      const obj = (o || {}) as Record<string, unknown>;
      const value = String(obj.value ?? obj.label ?? "");
      return {
        value,
        label: String(obj.label ?? obj.value ?? value),
        color: typeof obj.color === "string" ? obj.color : undefined,
      };
    })
    .filter((o) => o.value !== "");
};

// R4 — Listen-Unterwerte: der Zeilen-Baum reicht bis zu 4 Ebenen (L1..L4);
// muss mit MAX_SUBROW_DEPTH im Backend (storage/lists) übereinstimmen.
const MAX_LEVELS = 4;

// R4 — „Alle standardmäßig ausgeklappt" ist eine reine Anzeige-Präferenz der
// Ansicht (pro Nutzer, pro Liste) — im localStorage, defensiv gekapselt.
const expandDefaultKey = (fileId: string) =>
  `ryfe.list.expandDefault.${fileId}`;
const readExpandDefault = (fileId: string): boolean => {
  try {
    return localStorage.getItem(expandDefaultKey(fileId)) === "1";
  } catch {
    return false;
  }
};
const writeExpandDefault = (fileId: string, on: boolean) => {
  try {
    localStorage.setItem(expandDefaultKey(fileId), on ? "1" : "0");
  } catch {
    /* private mode / blocked storage — die Präferenz ist optional */
  }
};

export const RyfeList = ({ fileId }: { fileId: string }) => {
  const { workspace, workspaces, group } = useRyfe();
  // v1.8 AP-10 Inc 3b (D-140) — die aktive Gruppe (Scope) stempelt neue
  // Systemlisten-Zeilen; WS-Ebene (group=null) → WS-weiter Eintrag. Für
  // gewöhnliche Listen ignoriert der Server das Feld (bleibt NULL).
  const activeGroupId = group?.id ?? null;
  const [wsId, setWsId] = useState<string | null>(null);
  const [name, setName] = useState("Liste");
  const [docFolder, setDocFolder] = useState<string | null>(null);
  // A3b (D-158/D-160) — der Systemlisten-Typ der offenen Datei + die
  // Geschwister-Listen des CRM-Reiter-Containers (contacts/companies/persons).
  const [listType, setListType] = useState<string | null>(null);
  const [crmTabs, setCrmTabs] = useState<
    { id: string; listType: string; label: string }[]
  >([]);
  const [snap, setSnap] = useState<ListSnapshot | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [error, setError] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  // CC_11-Revision — the forms manager (the missing UI over the D-Forms backend).
  const [formsOpen, setFormsOpen] = useState(false);
  const { sort, toggle, indicator } = useTableSort();
  const [filter, setFilter] = useState<FilterConfig>(EMPTY_FILTER);
  // CC_07 Block 4 — column dialog (create / edit) + per-column menu + hidden set.
  const [colDialog, setColDialog] = useState<{
    col?: ListColumn;
    position: number;
  } | null>(null);
  // CC_07 Block 4 (fix) — the column menu is anchored to the ⋮ button in VIEWPORT
  // coords and rendered position:fixed, so the table's own `overflow:auto` scroll
  // container can no longer clip it (that was the „Menü öffnet nicht"-Defekt).
  const [colMenu, setColMenu] = useState<{
    id: string;
    x: number;
    y: number;
  } | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  // CC_07 Block 5 — column widths set by dragging the header edge.
  const [colWidths, setColWidths] = useState<Record<string, number>>({});
  // CC_08 — number/url cells display formatted; a click switches to raw edit.
  const [editCell, setEditCell] = useState<string | null>(null);
  // Zeilen-Detailview (Mockup v3 D) — the open row's id (data derived from snap).
  const [detailRowId, setDetailRowId] = useState<string | null>(null);
  // CC_11 Block 1 (D-69) — the ONE relation picker, opened per (row, column).
  const [relPicker, setRelPicker] = useState<{
    rowId: string;
    colId: string;
  } | null>(null);
  // P18 (Mockup) — display names of linked target rows per relation column:
  // { columnId: { rowId: display } }. Loaded once per target list so relation
  // cells render CHIPS with names instead of a bare count.
  const [relDisplays, setRelDisplays] = useState<
    Record<string, Record<string, string>>
  >({});
  // CC_11 Block 4 (D-72) — isolated column resize, global per user (default ON).
  const [isolatedResize, setIsolatedResize] = useState(true);
  const csvInput = useRef<HTMLInputElement | null>(null);
  // AP-11 (P19) — hidden Excel-input + das konsolidierte „⋯ Liste"-Menü.
  const xlsxInput = useRef<HTMLInputElement | null>(null);
  const [listMenu, setListMenu] = useState(false);
  // R4 — Listen-Unterwerte (Aufgaben-Hierarchie): Listeneinstellungen (shared,
  // „Unterwerte zulassen") + Ansichtseinstellungen (per Nutzer, „standardmäßig
  // ausgeklappt") + der aktuelle Ausklapp-Zustand des Zeilen-Baums.
  const [listSettingsOpen, setListSettingsOpen] = useState(false);
  const [viewSettingsOpen, setViewSettingsOpen] = useState(false);
  // Welle R — Anlage-Formular für gebundene Listen mit Pflichtfeldern (Prozesse:
  // `name`). Offen = Draft-Werte je Feld-id; sonst null.
  const [createDraft, setCreateDraft] = useState<Record<string, string> | null>(
    null,
  );
  // AP1 — „aus Prozess erstellen" (Aufgaben-Liste): Instanzierungs-Dialog offen?
  const [instantiateOpen, setInstantiateOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [defaultExpanded, setDefaultExpanded] = useState(() =>
    readExpandDefault(fileId),
  );
  // R4 — Browser-Tab trägt den Dateinamen.
  useDocumentTitle(name);

  useEffect(() => {
    let cancelled = false;
    ryfeApi
      .getSettings()
      .then((r) => {
        if (!cancelled) {
          setIsolatedResize(r.settings.columnResizeIsolated !== false);
        }
      })
      .catch(() => {
        /* keep the default */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // R4 (Punkt 18) — Deep-Link ?row=<id> (Chip-Doppelklick aus einer anderen
  // Liste): nach dem ersten Laden die Detailansicht der Zeile öffnen.
  const rowParamRef = useRef(false);
  useEffect(() => {
    if (rowParamRef.current || !snap) {
      return;
    }
    rowParamRef.current = true;
    try {
      const rid = new URLSearchParams(window.location.search).get("row");
      if (rid && snap.rows.some((r) => r.id === rid)) {
        setDetailRowId(rid);
      }
    } catch {
      /* Deep-Link optional */
    }
  }, [snap]);

  // R4 — „standardmäßig ausgeklappt": beim Laden (und wenn die Ansicht-Präferenz
  // an ist) alle Zeilen mit Kindern aufgeklappt initialisieren. Nur einmal je
  // Snapshot-Ladevorgang, damit manuelles Zuklappen danach bestehen bleibt.
  const expandInitRef = useRef(false);
  useEffect(() => {
    if (!snap) {
      expandInitRef.current = false;
      return;
    }
    if (expandInitRef.current) {
      return;
    }
    expandInitRef.current = true;
    if (defaultExpanded && snap.settings?.allow_subvalues) {
      const parents = new Set<string>();
      for (const r of snap.rows) {
        if (r.parent_row_id) {
          parents.add(r.parent_row_id);
        }
      }
      setExpanded(parents);
    }
  }, [snap, defaultExpanded]);

  const resolveWs = useCallback(async (): Promise<string | null> => {
    const active = workspace?.id;
    const candidates = [
      active,
      ...workspaces.map((w) => w.id).filter((id) => id !== active),
    ].filter((id): id is string => !!id);
    try {
      const { items } = await ryfeApi.sharedWithMe();
      for (const it of items) {
        if (!candidates.includes(it.workspaceId)) {
          candidates.push(it.workspaceId);
        }
      }
    } catch {
      /* optional */
    }
    for (const ws of candidates) {
      try {
        const meta = await ryfeApi.getFile(ws, fileId);
        setName(meta.name);
        setDocFolder((meta as { folder_id?: string }).folder_id ?? null);
        setListType(meta.list_type ?? null);
        return ws;
      } catch {
        /* next */
      }
    }
    return null;
  }, [workspace?.id, workspaces, fileId]);

  const load = useCallback(
    async (ws: string) => {
      try {
        setSnap(await ryfeApi.getList(ws, fileId));
        setStatus("ready");
      } catch (e) {
        setError((e as Error).message);
        setStatus("error");
      }
    },
    [fileId],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ws = await resolveWs();
      if (cancelled) {
        return;
      }
      if (!ws) {
        setStatus("error");
        return;
      }
      setWsId(ws);
      await load(ws);
    })();
    return () => {
      cancelled = true;
    };
  }, [resolveWs, load]);

  // A3b (D-158/D-160) — ist die offene Liste eine CRM-Entitätsliste, die
  // Geschwister-Reiter (contacts/companies/persons) des Workspace auflösen. Die
  // drei bleiben eigene gebundene Systemlisten (Weg 2, kein Backend-Rewrite);
  // der Reiter-Kopf vereint sie zu EINER CRM-Erfahrung.
  useEffect(() => {
    if (!wsId || !isCrmTabType(listType)) {
      setCrmTabs([]);
      return;
    }
    let cancelled = false;
    ryfeApi
      .listWorkspaceLists(wsId)
      .then((r) => {
        if (cancelled) {
          return;
        }
        const byType = new Map(r.lists.map((l) => [l.list_type ?? "", l]));
        const tabs = CRM_TAB_TYPES.flatMap((t) => {
          const f = byType.get(t.listType);
          return f ? [{ id: f.id, listType: t.listType, label: t.label }] : [];
        });
        // Nur anzeigen, wenn wenigstens zwei Reiter auflösbar sind (sonst ist es
        // keine „Reiter"-Erfahrung, z. B. eine einzelne, umbenannte Liste).
        setCrmTabs(tabs.length >= 2 ? tabs : []);
      })
      .catch(() => {
        if (!cancelled) {
          setCrmTabs([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [wsId, listType]);

  // CC_07 Block 4 — close the open column menu on any outside click.
  useEffect(() => {
    if (!colMenu) {
      return;
    }
    const close = () => setColMenu(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [colMenu]);

  // AP-11 (P19) — close the consolidated list menu on any outside click.
  useEffect(() => {
    if (!listMenu) {
      return;
    }
    const close = () => setListMenu(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [listMenu]);

  // CC_07 Block 4 — persist a column draft from the dialog: edit an existing
  // column (Typ-Config) or create a new one at the given position.
  const submitColumn = async (draft: ColumnDraft) => {
    if (!wsId || !colDialog) {
      return;
    }
    try {
      if (colDialog.col) {
        await ryfeApi.updateListColumn(wsId, fileId, colDialog.col.id, draft);
      } else {
        await ryfeApi.createListColumn(wsId, fileId, {
          ...draft,
          position: colDialog.position,
        });
      }
      setColDialog(null);
      await load(wsId);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const removeColumn = async (col: ListColumn) => {
    if (!wsId || !window.confirm(`Spalte „${col.name}“ löschen?`)) {
      return;
    }
    try {
      await ryfeApi.deleteListColumn(wsId, fileId, col.id);
      await load(wsId);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  // AP-6 (P7.2 / D-137) — opt-in backfill of an auto-create column over existing
  // rows. Above the server threshold the first call returns 409 with the count;
  // we confirm and retry with confirm=true.
  const backfillColumn = async (col: ListColumn) => {
    if (!wsId) {
      return;
    }
    try {
      const r = await ryfeApi.backfillListColumn(wsId, fileId, col.id);
      await load(wsId);
      window.alert(
        r.created > 0
          ? `${r.created} Datei(en) nachgelegt.`
          : "Keine Zeilen ohne Datei — nichts nachzulegen.",
      );
    } catch (e) {
      const err = e as ApiError;
      if (err.code === "CONFIRM_REQUIRED") {
        if (
          window.confirm(
            `${err.count} Zeilen ohne Datei — jetzt für alle nachlegen? Das kann einen Moment dauern.`,
          )
        ) {
          try {
            const r = await ryfeApi.backfillListColumn(
              wsId,
              fileId,
              col.id,
              true,
            );
            await load(wsId);
            window.alert(`${r.created} Datei(en) nachgelegt.`);
          } catch (e2) {
            setError((e2 as Error).message);
          }
        }
        return;
      }
      setError((e as Error).message);
    }
  };

  // CC_07 Block 5 — rename this list file from its own page (Detailview).
  const renameFile = async () => {
    if (!wsId) {
      return;
    }
    const next = window.prompt("Liste umbenennen", name);
    if (!next || next === name) {
      return;
    }
    try {
      await ryfeApi.patchFile(wsId, fileId, { name: next });
      setName(next);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  // CC_07 Block 5 — drag the header edge to resize a column (client-side width).
  const startResize = (e: React.MouseEvent, colId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const th = (e.currentTarget as HTMLElement).parentElement;
    const startX = e.clientX;
    const startW =
      colWidths[colId] ?? (th ? th.getBoundingClientRect().width : 120);
    const onMove = (ev: MouseEvent) => {
      const w = Math.max(60, startW + (ev.clientX - startX));
      setColWidths((m) => ({ ...m, [colId]: w }));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // CSV-Import (Upload, Kommentare C): Kopfzeile → Spalten (nach Name; fehlende
  // werden als Text angelegt), Datenzeilen → neue Zeilen. Die System-Spalten
  // row_id/UUID in der Kopfzeile werden übersprungen (read-only).
  const importCSV = async (file: File) => {
    if (!wsId || !snap) {
      return;
    }
    try {
      // Revision R3 — drei reale Import-Defekte: (1) Excel/Windows-CSV ist oft
      // CP-1252 (Umlaute zerfielen als UTF-8 zu U+FFFD) und trägt ein BOM;
      // (2) deutsche Excel-CSVs trennen mit `;` (alles landete in EINER
      // Spalte); (3) position:0 je Zeile kehrte die Import-Reihenfolge um.
      const text = decodeCsvBuffer(await file.arrayBuffer());
      const matrix = parseCSV(text, detectDelimiter(text));
      await importMatrix(matrix);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  // AP-11 (P19) — Excel-Import: `.xlsx` → dieselbe Matrix-Pipeline wie CSV.
  const importXlsx = async (file: File) => {
    if (!wsId || !snap) {
      return;
    }
    try {
      const matrix = xlsxToMatrix(new Uint8Array(await file.arrayBuffer()));
      await importMatrix(matrix);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  // AP-11 (P19) — gemeinsamer Import-Kern: Kopfzeile → Spalten (nach Name;
  // fehlende als Text), Datenzeilen → neue Zeilen. row_id/UUID werden als
  // read-only System-Spalten übersprungen. Von CSV- UND Excel-Import genutzt.
  const importMatrix = async (matrix: string[][]) => {
    if (!wsId || !snap) {
      return;
    }
    try {
      if (matrix.length < 2) {
        return;
      }
      const headers = matrix[0].map((h) => h.trim());
      const byName = new Map(snap.columns.map((c) => [c.name, c]));
      const targets: (ListColumn | null)[] = [];
      for (const h of headers) {
        if (h === "row_id" || h === "UUID") {
          targets.push(null);
          continue;
        }
        let col = byName.get(h) || null;
        if (!col) {
          col = await ryfeApi.createListColumn(wsId, fileId, {
            name: h,
            type: "text",
            position: snap.columns.length + targets.filter(Boolean).length,
          });
          byName.set(h, col);
        }
        targets.push(col);
      }
      const basePos = snap.rows.length;
      for (let r = 1; r < matrix.length; r++) {
        const cells = matrix[r];
        const data: Record<string, unknown> = {};
        targets.forEach((col, i) => {
          if (col && cells[i] !== undefined && cells[i] !== "") {
            data[col.id] = cells[i];
          }
        });
        await ryfeApi.createListRow(wsId, fileId, {
          data,
          position: basePos + (r - 1),
          group_id: activeGroupId,
        });
      }
      await load(wsId);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const addRow = async () => {
    if (!wsId) {
      return;
    }
    try {
      const created = await ryfeApi.createListRow(wsId, fileId, {
        data: {},
        position: snap?.rows.length ?? 0,
        group_id: activeGroupId,
      });
      await load(wsId);
      // Open the fresh row straight in the detail pop-up so it can be filled in
      // right away (esp. on mobile, where inline cell editing is fiddly).
      setDetailRowId(created.id);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  // Welle R — „+ Zeile" für eine gebundene Liste mit Pflichtfeldern (Prozesse):
  // ein leeres Insert würde serverseitig mit 400 abbrechen, deshalb erst ein
  // Anlage-Formular mit den vom Adapter gemeldeten `create_fields` sammeln.
  const createFields = snap?.settings?.create_fields || null;
  const openCreate = () => {
    if (!createFields) {
      void addRow();
      return;
    }
    // Draft mit den Defaults der Select-Felder (erste Option) vorbelegen.
    const draft: Record<string, string> = {};
    for (const f of createFields) {
      if (f.type === "select" && f.options && f.options.length) {
        draft[f.id] = f.options[0];
      }
    }
    setCreateDraft(draft);
  };
  // Ein konditionales Feld (showIf) ist nur sichtbar/relevant, wenn das
  // steuernde Feld den passenden Wert trägt (z. B. RRULE/Start nur bei iterativ).
  const fieldVisible = (f: { showIf?: { field: string; equals: string } }) =>
    !f.showIf || (createDraft?.[f.showIf.field] ?? "") === f.showIf.equals;
  const DEFAULT_RRULE = "FREQ=WEEKLY;INTERVAL=1";
  const submitCreate = async () => {
    if (!wsId || !createFields || !createDraft) {
      return;
    }
    // Pflichtfelder prüfen — nur sichtbare; ein RRULE-Feld hat immer eine gültige
    // Standard-Regel, gilt also als ausgefüllt.
    const missing = createFields.find(
      (f) =>
        f.required &&
        fieldVisible(f) &&
        f.type !== "rrule" &&
        !String(createDraft[f.id] || "").trim(),
    );
    if (missing) {
      setError(`Pflichtfeld fehlt: ${missing.label}`);
      return;
    }
    try {
      const data: Record<string, string> = {};
      for (const f of createFields) {
        if (!fieldVisible(f)) {
          continue; // z. B. rrule/start_date bei on_demand nicht mitsenden
        }
        const v =
          f.type === "rrule"
            ? String(createDraft[f.id] || DEFAULT_RRULE)
            : String(createDraft[f.id] || "").trim();
        if (v) {
          data[f.id] = v;
        }
      }
      const created = await ryfeApi.createListRow(wsId, fileId, {
        data,
        position: snap?.rows.length ?? 0,
        group_id: activeGroupId,
      });
      setCreateDraft(null);
      await load(wsId);
      setDetailRowId(created.id);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  // Quicklink `?create=1` (z. B. „Prozess erstellen" aus dem Gruppen-Dashboard):
  // öffnet den Anlage-Dialog automatisch, sobald die gebundene Liste geladen ist.
  const createAutoOpened = useRef(false);
  useEffect(() => {
    if (createAutoOpened.current || !snap) {
      return;
    }
    const wants =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("create");
    if (wants && snap.settings?.create_fields) {
      createAutoOpened.current = true;
      openCreate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snap]);

  // R4 — eine Unterwert-Zeile (L(n+1)) unter einer bestehenden Zeile anlegen;
  // der Parent wird direkt aufgeklappt, damit die neue Zeile sichtbar ist.
  const addSubRow = async (parentId: string) => {
    if (!wsId) {
      return;
    }
    try {
      const created = await ryfeApi.createListRow(wsId, fileId, {
        data: {},
        parent_row_id: parentId,
        group_id: activeGroupId,
      });
      setExpanded((prev) => new Set(prev).add(parentId));
      await load(wsId);
      setDetailRowId(created.id);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const toggleExpanded = (rowId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });

  // R4 — Listeneinstellung „Unterwerte zulassen" umschalten (shared, Backend).
  const setAllowSubvalues = async (allow: boolean) => {
    if (!wsId) {
      return;
    }
    try {
      await ryfeApi.setListSettings(wsId, fileId, { allow_subvalues: allow });
      await load(wsId);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  // P18 — resolve chip labels: one target-list fetch per row-mode relation
  // column (title column → fallback row id). Re-runs when the snapshot reloads
  // (e.g. after linking a freshly created row).
  useEffect(() => {
    if (!wsId || !snap) {
      return;
    }
    let cancelled = false;
    const relCols = snap.columns.filter(
      (c) => c.type === "relation" && (c.config?.mode ?? "row") === "row",
    );
    (async () => {
      const next: Record<string, Record<string, string>> = {};
      for (const col of relCols) {
        const targetId = (col.config?.targetListId as string) || fileId;
        try {
          const target =
            targetId === fileId ? snap : await ryfeApi.getList(wsId, targetId);
          const titleCol = target.columns.find((c) => c.type === "text");
          const map: Record<string, string> = {};
          for (const r of target.rows) {
            const v = titleCol ? r.data[titleCol.id] : undefined;
            map[r.id] =
              v !== undefined && v !== null && String(v).trim()
                ? String(v)
                : r.id.slice(0, 8);
          }
          next[col.id] = map;
        } catch {
          next[col.id] = {};
        }
      }
      if (!cancelled) {
        setRelDisplays(next);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [wsId, fileId, snap]);

  const saveCell = async (row: ListRow, col: ListColumn, value: unknown) => {
    if (!wsId) {
      return;
    }
    const data = { ...row.data, [col.id]: value };
    try {
      await ryfeApi.updateListRow(wsId, fileId, row.id, { data });
      setSnap((s) =>
        s
          ? {
              ...s,
              rows: s.rows.map((r) => (r.id === row.id ? { ...r, data } : r)),
            }
          : s,
      );
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const removeRow = async (row: ListRow) => {
    if (!wsId || !window.confirm("Diese Zeile löschen?")) {
      return;
    }
    try {
      await ryfeApi.deleteListRow(wsId, fileId, row.id);
      await load(wsId);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  if (status === "loading") {
    return <div className="ryfe-muted">Liste lädt…</div>;
  }
  if (status === "error" || !snap) {
    return <div className="ryfe-auth__error">Liste nicht gefunden.</div>;
  }

  const cellVal = (r: ListRow, key: string) => r.data[key];
  const flatRows = applyFilter(
    sortRows(snap.rows, sort, cellVal),
    filter,
    cellVal,
  );
  const visibleColumns = snap.columns.filter((c) => !hidden.has(c.id));

  // R4 — Unterwert-Baum. Ist „Unterwerte zulassen" an, rendern wir eine
  // eingerückte Hierarchie statt der flachen Liste: Sortierung greift je
  // Geschwister-Gruppe, der Filter behält Treffer INKL. ihrer Vorfahren (sonst
  // verschwände die Elternzeile) und klappt bei aktivem Filter alles auf.
  // Weg 2 / A1 — gebundene Systemliste: feste Adapter-Spalten und ggf. kein
  // Direkt-Create (kein „+ Zeile", z. B. Aufgaben/Kontakte).
  const isBound = !!snap.settings?.bound;
  const canCreateRow = snap.settings?.can_create !== false;
  // Redesign Welle R — „Achse": zusätzliche CUSTOM-Spalten (Link/URL/Text …) sind
  // wie in „Meine Dateien" auch auf gebundenen Systemlisten erlaubt, sobald der
  // Server `custom_columns` signalisiert. „+ Spalte" also nicht mehr nur bei
  // ungebundenen Listen zeigen.
  const canAddColumn = !isBound || !!snap.settings?.custom_columns;
  // Weg 2 / A2 (D-159) — eine gebundene Liste kann den Baum über eine Self-
  // Relation-SPALTE ziehen (Prozesse: `parent_process_id`) statt über die
  // list_rows-Unterwerte. Die Spaltenwerte sind Zeilen-IDs (row.id == entity.id).
  const treeParentCol = snap.settings?.tree_parent_col || null;
  const parentKeyOf = (r: ListRow): string =>
    treeParentCol
      ? String((r.data && r.data[treeParentCol]) || "")
      : r.parent_row_id || "";
  const isTree = !!snap.settings?.allow_subvalues || !!treeParentCol;
  const hasFilter = filter.conditions.length > 0;
  const childrenOf = new Map<string, ListRow[]>();
  for (const r of snap.rows) {
    const key = parentKeyOf(r);
    (childrenOf.get(key) || childrenOf.set(key, []).get(key)!).push(r);
  }
  const byId = new Map(snap.rows.map((r) => [r.id, r]));
  // Filter-Trefferset inkl. Vorfahren (nur relevant im Tree-Modus mit Filter).
  const keepIds = new Set<string>();
  if (isTree && hasFilter) {
    for (const m of flatRows) {
      let cur: ListRow | undefined = m;
      while (cur) {
        if (keepIds.has(cur.id)) {
          break;
        }
        keepIds.add(cur.id);
        const pk = parentKeyOf(cur);
        cur = pk ? byId.get(pk) : undefined;
      }
    }
  }
  const passes = (r: ListRow) => !hasFilter || keepIds.has(r.id);
  const treeRows: { row: ListRow; depth: number; hasKids: boolean }[] = [];
  const walk = (parentKey: string, depth: number) => {
    const kids = sortRows(
      (childrenOf.get(parentKey) || []).filter(passes),
      sort,
      cellVal,
    );
    for (const r of kids) {
      const hasKids = (childrenOf.get(r.id) || []).some(passes);
      treeRows.push({ row: r, depth, hasKids });
      const open = expanded.has(r.id) || hasFilter;
      if (hasKids && open && depth < MAX_LEVELS) {
        walk(r.id, depth + 1);
      }
    }
  };
  if (isTree) {
    walk("", 1);
  }
  // Einheitliche Render-Liste: im Tree-Modus die Hierarchie, sonst flach (L1).
  const displayRows: { row: ListRow; depth: number; hasKids: boolean }[] =
    isTree
      ? treeRows
      : flatRows.map((r) => ({ row: r, depth: 1, hasKids: false }));

  const renderCell = (
    row: ListRow,
    col: ListColumn,
    ctx: "table" | "detail" = "table",
  ) => {
    const v = row.data[col.id];
    if (col.type === "relation") {
      const mode = (col.config?.mode as string) || "row";
      if (mode === "board" && typeof v === "string" && v) {
        // AP-6 (P7.1) — the auto-create column may hold a board, note, list or
        // folder (config.create_kind). Route + label by the created kind instead
        // of always opening the board editor (which mis-rendered md/list rows).
        const createKind = (col.config?.create_kind as string) || "board";
        const target =
          createKind === "md"
            ? `/md/${v}`
            : createKind === "list"
            ? `/list/${v}`
            : createKind === "folder"
            ? `/files/${v}`
            : `/board/${v}`;
        const label =
          createKind === "md"
            ? "Notiz öffnen ↗"
            : createKind === "list"
            ? "Liste öffnen ↗"
            : createKind === "folder"
            ? "Ordner öffnen ↗"
            : "Whiteboard öffnen ↗";
        return (
          <button className="ryfe-linkish" onClick={() => navigate(target)}>
            {label}
          </button>
        );
      }
      // P18 (Mockup) — linked rows render as CHIPS with name + ✕ to unlink;
      // „＋ Verknüpfen…" opens the shared picker (D-69).
      const linked = row.links?.[col.id] || [];
      const names = relDisplays[col.id] || {};
      // R4 (Punkt 18) — Doppelklick auf einen Chip öffnet die Detailansicht
      // der ZIEL-Zeile: in derselben Liste direkt, sonst per Deep-Link
      // /list/<ziel>?row=<id> (die Ziel-Liste öffnet das Detail beim Laden).
      const chipTarget = (col.config?.targetListId as string) || fileId;
      const openChipDetail = (id: string) => {
        if (chipTarget === fileId) {
          setDetailRowId(id);
        } else {
          navigate(`/list/${chipTarget}?row=${id}`);
        }
      };
      return (
        <span className="ryfe-relcell">
          {linked.map((id) => (
            <span
              key={id}
              className="ryfe-chip"
              title="Doppelklick: Detailansicht öffnen"
              onDoubleClick={() => openChipDetail(id)}
            >
              {names[id] ?? id.slice(0, 8)}
              <button
                className="ryfe-chip__x"
                title="Verknüpfung lösen"
                onClick={() =>
                  wsId &&
                  void ryfeApi
                    .updateListRow(wsId, fileId, row.id, {
                      links: {
                        [col.id]: linked.filter((other) => other !== id),
                      },
                    })
                    .then(() => load(wsId))
                }
              >
                ✕
              </button>
            </span>
          ))}
          <button
            className="ryfe-linkish"
            title="Verknüpfungen bearbeiten"
            data-testid={`rel-open-${col.id}`}
            onClick={() => setRelPicker({ rowId: row.id, colId: col.id })}
          >
            ＋ Verknüpfen…
          </button>
        </span>
      );
    }
    // CC_11 Block 6 (D-56) — a webhooks column is config-only: its hooks fire
    // on row events; the cell itself carries no value.
    if (col.type === "webhooks") {
      const n = Array.isArray(col.config?.hooks)
        ? (col.config.hooks as unknown[]).length
        : 0;
      return <span className="ryfe-muted">{n} Hook(s)</span>;
    }
    if (col.type === "checkbox") {
      return (
        <input
          type="checkbox"
          checked={!!v}
          onChange={(e) => saveCell(row, col, e.target.checked)}
        />
      );
    }
    // CC_07 Block 1 (D-40) — select renders as a real dropdown from
    // config.options (was a generic text input). An unknown stored value never
    // breaks: it is offered as an extra option so the box still shows it.
    if (col.type === "select") {
      const options = readSelectOptions(col.config);
      const cur = v === undefined || v === null ? "" : String(v);
      const known = options.some((o) => o.value === cur);
      const active = options.find((o) => o.value === cur);
      const selKey = `${row.id}:${col.id}`;
      // Read mode (Mockup v3): a colored chip showing the OPTION VALUE (dot +
      // label) — not a bare native select whose value got hidden behind a
      // border. A click switches to the dropdown for editing.
      if (editCell !== selKey) {
        return (
          <span
            className="ryfe-list__pill ryfe-list__disp"
            title="Bearbeiten"
            onClick={() => setEditCell(selKey)}
          >
            {cur ? (
              <>
                <span
                  className="ryfe-list__dot"
                  style={{ background: active?.color || "#cbd5e1" }}
                />
                {active?.label || cur}
              </>
            ) : (
              <span className="ryfe-muted">—</span>
            )}
          </span>
        );
      }
      return (
        <select
          className="ryfe-field ryfe-list__cell"
          value={cur}
          autoFocus
          onChange={(e) => {
            setEditCell(null);
            saveCell(row, col, e.target.value === "" ? null : e.target.value);
          }}
          onBlur={() => setEditCell(null)}
        >
          <option value="">—</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
          {cur && !known && <option value={cur}>{cur}</option>}
        </select>
      );
    }
    const editKey = `${row.id}:${col.id}`;
    // CC_08 — number renders formatted (Separatoren/Währung aus config); a click
    // switches to the raw input for editing.
    if (col.type === "number") {
      if (editCell === editKey) {
        return (
          <input
            className="ryfe-field ryfe-list__cell"
            type="number"
            autoFocus
            defaultValue={v === undefined || v === null ? "" : String(v)}
            onBlur={(e) => {
              const next =
                e.target.value === "" ? null : Number(e.target.value);
              setEditCell(null);
              if (next !== v) {
                saveCell(row, col, next);
              }
            }}
          />
        );
      }
      return (
        <span
          className="ryfe-list__disp"
          title="Bearbeiten"
          onClick={() => setEditCell(editKey)}
        >
          {formatNumber(v, col.config as NumberFormat) || "—"}
        </span>
      );
    }
    // CC_08 — url renders as a link or a button with an optional label; a click
    // on the ✎ (or the empty placeholder) switches to the raw input.
    if (col.type === "url") {
      if (editCell === editKey) {
        return (
          <input
            className="ryfe-field ryfe-list__cell"
            type="url"
            autoFocus
            defaultValue={v === undefined || v === null ? "" : String(v)}
            onBlur={(e) => {
              const next = e.target.value;
              setEditCell(null);
              if (next !== v) {
                saveCell(row, col, next);
              }
            }}
          />
        );
      }
      if (!v) {
        return (
          <span
            className="ryfe-list__disp ryfe-muted"
            title="URL eingeben"
            onClick={() => setEditCell(editKey)}
          >
            —
          </span>
        );
      }
      const view = urlView(col.config as UrlFormat);
      const text = urlLabelFor(v, col.config as UrlFormat);
      return (
        <span className="ryfe-list__urlcell">
          <a
            className={view.display === "button" ? "ryfe-chip" : "ryfe-linkish"}
            href={String(v)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {text}
          </a>
          <button
            type="button"
            className="ryfe-th__menu"
            title="Bearbeiten"
            onClick={() => setEditCell(editKey)}
          >
            ✎
          </button>
        </span>
      );
    }
    // url_text (P22): Anzeigetext + separates Link-Ziel. CC_11-Revision: der
    // Edit-Zweig fehlte — der Klick setzte nur den Edit-State, ohne dass je ein
    // Editor erschien, und gefüllte Zellen hatten gar keinen Edit-Einstieg.
    if (col.type === "url_text") {
      const { text, url } = urlTextCell(v);
      const label = urlTextLabel(v);
      if (editCell === editKey) {
        let draftText = text;
        let draftUrl = url;
        const commit = () => {
          setEditCell(null);
          const t = draftText.trim();
          const u = draftUrl.trim();
          if (t === text && u === url) {
            return;
          }
          saveCell(row, col, t || u ? { text: t, url: u } : "");
        };
        return (
          <span className="ryfe-list__urlcell">
            <input
              className="ryfe-field ryfe-list__cell"
              autoFocus
              placeholder="Text"
              defaultValue={text}
              onChange={(e) => {
                draftText = e.target.value;
              }}
              onKeyDown={(e) => e.key === "Enter" && commit()}
              onBlur={(e) => {
                // Nur committen, wenn der Fokus die Zelle wirklich verlässt
                // (nicht beim Wechsel ins URL-Feld daneben).
                if (
                  !e.relatedTarget ||
                  !(e.currentTarget.parentElement as HTMLElement).contains(
                    e.relatedTarget as Node,
                  )
                ) {
                  commit();
                }
              }}
            />
            <input
              className="ryfe-field ryfe-list__cell"
              placeholder="https://…"
              defaultValue={url}
              onChange={(e) => {
                draftUrl = e.target.value;
              }}
              onKeyDown={(e) => e.key === "Enter" && commit()}
              onBlur={(e) => {
                if (
                  !e.relatedTarget ||
                  !(e.currentTarget.parentElement as HTMLElement).contains(
                    e.relatedTarget as Node,
                  )
                ) {
                  commit();
                }
              }}
            />
          </span>
        );
      }
      if (!url && !label) {
        return (
          <span
            className="ryfe-list__disp ryfe-muted"
            onClick={() => setEditCell(editKey)}
          >
            —
          </span>
        );
      }
      return (
        <span className="ryfe-list__urlcell">
          {url ? (
            <a
              className="ryfe-linkish"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {label}
            </a>
          ) : (
            <span className="ryfe-list__disp">{label}</span>
          )}
          <button
            className="ryfe-btn ryfe-btn--ghost"
            title="Bearbeiten"
            onClick={() => setEditCell(editKey)}
          >
            ✎
          </button>
        </span>
      );
    }
    // color (P22, CC_09 Render-Hälfte): Farbtupfer + Hex; native color-Editor.
    if (col.type === "color") {
      const hex = normalizeColor(v);
      if (editCell === editKey) {
        return (
          <input
            className="ryfe-field ryfe-list__cell"
            type="color"
            autoFocus
            defaultValue={hex || "#000000"}
            onBlur={(e) => {
              setEditCell(null);
              const next = normalizeColor(e.target.value);
              if (next !== hex) {
                saveCell(row, col, next);
              }
            }}
          />
        );
      }
      return (
        <span
          className="ryfe-list__disp ryfe-list__colorcell"
          onClick={() => setEditCell(editKey)}
        >
          {hex ? (
            <>
              <span
                className="ryfe-list__swatch"
                style={{ background: hex }}
                aria-hidden="true"
              />
              {hex}
            </>
          ) : (
            <span className="ryfe-muted">—</span>
          )}
        </span>
      );
    }
    // CC_11-Revision — Long-Text im Detailview: Text-Spalten (insbesondere mit
    // config.multiline) bekommen dort eine wachsende Textarea statt der
    // einzeiligen Tabellen-Zelle.
    if (
      col.type === "text" &&
      ctx === "detail" &&
      (col.config?.multiline === true ||
        String(v ?? "").length > 80 ||
        String(v ?? "").includes("\n"))
    ) {
      return (
        <textarea
          className="ryfe-field ryfe-detail__textarea"
          rows={Math.min(
            12,
            Math.max(4, String(v ?? "").split("\n").length + 1),
          )}
          defaultValue={v === undefined || v === null ? "" : String(v)}
          onBlur={(e) => {
            const next = e.target.value;
            if (next !== v) {
              saveCell(row, col, next);
            }
          }}
        />
      );
    }
    const inputType = col.type === "date" ? "date" : "text";
    return (
      <input
        className="ryfe-field ryfe-list__cell"
        type={inputType}
        defaultValue={v === undefined || v === null ? "" : String(v)}
        onBlur={(e) => {
          const next = e.target.value;
          if (next !== v) {
            saveCell(row, col, next);
          }
        }}
      />
    );
  };

  return (
    <div className="ryfe-board">
      <div className="ryfe-board__bar">
        <button
          className="ryfe-btn ryfe-btn--ghost"
          onClick={() => navigate(backToFiles(docFolder))}
          title="Zurück in den Ordner der Datei"
        >
          ← Dateien
        </button>
        <span className="ryfe-board__bar__title">
          {crmTabs.length >= 2 ? "CRM" : name}
        </span>
        <button
          className="ryfe-btn ryfe-btn--ghost"
          title="Liste umbenennen"
          onClick={renameFile}
        >
          ✎
        </button>
        <FilterBar
          scope={`list:${fileId}`}
          fields={snap.columns.map((c) => ({
            key: c.id,
            label: c.name,
            type: c.type,
            options:
              c.type === "select" ? readSelectOptions(c.config) : undefined,
          }))}
          config={filter}
          onChange={setFilter}
        />
        <span className="ryfe-topbar__spacer" />
        {/* Versteckte Import-Inputs (CSV + Excel). */}
        <input
          ref={csvInput}
          type="file"
          accept=".csv,text/csv"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) {
              void importCSV(f);
            }
          }}
        />
        <input
          ref={xlsxInput}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) {
              void importXlsx(f);
            }
          }}
        />
        <button
          className="ryfe-btn ryfe-btn--ghost"
          title="Teilen"
          onClick={() => setShareOpen(true)}
        >
          Teilen
        </button>
        {/* AP-11 (P19) — EIN konsolidiertes Listen-Menü statt verstreuter Buttons:
            Import/Export (Excel/CSV/PDF), Spalten, Einstellungen, Formulare. */}
        <div className="ryfe-navgroup">
          <button
            className="ryfe-btn ryfe-btn--ghost"
            aria-haspopup="true"
            aria-expanded={listMenu}
            data-testid="list-menu"
            onClick={(e) => {
              e.stopPropagation();
              setListMenu((o) => !o);
            }}
          >
            ⋯ Liste
          </button>
          {listMenu && (
            <ul
              className="ryfe-context ryfe-context--col"
              style={{ right: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {canAddColumn && (
                <li>
                  <button
                    onClick={() => {
                      setColDialog({ position: snap?.columns.length ?? 0 });
                      setListMenu(false);
                    }}
                  >
                    + Spalte
                  </button>
                </li>
              )}
              <li>
                <button
                  onClick={() => {
                    xlsxInput.current?.click();
                    setListMenu(false);
                  }}
                >
                  ⬆ Excel importieren
                </button>
              </li>
              <li>
                <button
                  data-testid="export-xlsx"
                  onClick={() => {
                    downloadBytes(
                      safeFilename(name, "xlsx"),
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                      matrixToXlsx([
                        ["row_id", "UUID", ...snap.columns.map((c) => c.name)],
                        ...snap.rows.map((r) => [
                          String(r.seq ?? ""),
                          r.id,
                          ...snap.columns.map((c) =>
                            r.data[c.id] == null ? "" : String(r.data[c.id]),
                          ),
                        ]),
                      ]),
                    );
                    setListMenu(false);
                  }}
                >
                  ⬇ Excel exportieren
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    csvInput.current?.click();
                    setListMenu(false);
                  }}
                >
                  ⬆ CSV importieren
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    downloadText(
                      safeFilename(name, "csv"),
                      "text/csv",
                      toCSV(snap.columns, snap.rows),
                    );
                    setListMenu(false);
                  }}
                >
                  ⬇ CSV exportieren
                </button>
              </li>
              <li>
                <button
                  onClick={() => {
                    downloadBytes(
                      safeFilename(name, "pdf"),
                      "application/pdf",
                      textToPdf(name, listToPdfLines(snap.columns, snap.rows)),
                    );
                    setListMenu(false);
                  }}
                >
                  ⬇ PDF exportieren
                </button>
              </li>
              <li>
                <button
                  data-testid="forms-open"
                  onClick={() => {
                    setFormsOpen(true);
                    setListMenu(false);
                  }}
                >
                  Formulare
                </button>
              </li>
              <li>
                <button
                  data-testid="list-settings-open"
                  onClick={() => {
                    setListSettingsOpen(true);
                    setListMenu(false);
                  }}
                >
                  ⚙ Listeneinstellungen
                </button>
              </li>
              <li>
                <button
                  data-testid="view-settings-open"
                  onClick={() => {
                    setViewSettingsOpen(true);
                    setListMenu(false);
                  }}
                >
                  ⚙ Ansichtseinstellungen
                </button>
              </li>
            </ul>
          )}
        </div>
        {hidden.size > 0 && (
          <button
            className="ryfe-btn ryfe-btn--ghost"
            title="Ausgeblendete Spalten wieder einblenden"
            onClick={() => setHidden(new Set())}
          >
            {hidden.size} ausgeblendet ⤢
          </button>
        )}
        {canCreateRow && (
          <button className="ryfe-btn ryfe-btn--primary" onClick={openCreate}>
            + {createFields ? "Neu" : "Zeile"}
          </button>
        )}
        {/* AP1 — Aufgaben entstehen aus Prozessen: „＋ aus Prozess" statt Direkt-Create. */}
        {snap.settings?.materialize && (
          <button
            className="ryfe-btn ryfe-btn--primary"
            onClick={() => setInstantiateOpen(true)}
          >
            ＋ aus Prozess
          </button>
        )}
      </div>

      {/* A3b (D-158a/D-160) — CRM-Reiter: EINE CRM-Systemliste, drei Reiter im
          Editor-Kopf (contacts/companies/persons) über den entity_type-Adapter.
          Kein Shell-Nav, keine getrennten Dateien in der Nutzer-Erfahrung. */}
      {crmTabs.length >= 2 && (
        <nav className="ryfe-tabs" role="tablist" aria-label="CRM-Reiter">
          {crmTabs.map((t) => {
            const active = t.listType === listType;
            return (
              <button
                key={t.listType}
                role="tab"
                aria-selected={active}
                className={`ryfe-navlink${
                  active ? " ryfe-navlink--active" : ""
                }`}
                onClick={() => {
                  if (!active) {
                    navigate(`/list/${t.id}`);
                  }
                }}
              >
                {t.label}
              </button>
            );
          })}
        </nav>
      )}

      {error && <div className="ryfe-auth__error">{error}</div>}

      <div className="ryfe-page" style={{ maxWidth: "none" }}>
        {snap.columns.length === 0 ? (
          <p className="ryfe-muted">
            Noch keine Spalten. „+ Spalte" legt die erste an.
          </p>
        ) : (
          <div className="ryfe-table-wrap">
            {/* CC_11 Block 4 (D-72) — isolated resize: fixed table layout, so a
                dragged width changes ONLY that column (no redistribution). */}
            <table
              className={`ryfe-table ryfe-table--list${
                isolatedResize ? " ryfe-table--isolated" : ""
              }`}
            >
              <thead>
                <tr>
                  {/* CC_07 Block 2 (D-41) — read-only system columns. The stable
                      per-list number `seq` is shown as „row_id"; the UUID id is
                      shown too. Both are display-only (no editor). */}
                  <th className="ryfe-list__sys" title="Stabile Zeilennummer">
                    row_id
                  </th>
                  <th className="ryfe-list__sys" title="Zeilen-UUID">
                    UUID
                  </th>
                  {visibleColumns.map((c) => (
                    <th
                      key={c.id}
                      className="ryfe-th--sortable ryfe-th--col"
                      style={
                        colWidths[c.id]
                          ? {
                              width: colWidths[c.id],
                              minWidth: colWidths[c.id],
                            }
                          : undefined
                      }
                    >
                      <div className="ryfe-th__inner">
                        {/* Name = dezenter Sortier-Trigger (Klick sortiert, ▲/▼
                            nur auf der aktiven Spalte). Der Typ ist absichtlich
                            NICHT mehr im Kopf — er steht in „Einstellungen". */}
                        <button
                          type="button"
                          className="ryfe-th__name"
                          onClick={() => toggle(c.id)}
                          title="Nach dieser Spalte sortieren"
                        >
                          {c.name}
                          <span className="ryfe-th__ind">
                            {indicator(c.id)}
                          </span>
                        </button>
                        {/* ⋮ direkt über der Spalte — öffnet das schlanke
                            Spalten-Menü (Einstellungen / Spalte löschen). Feste
                            Adapter-Spalten (locked, z. B. „Aufgabe"/„Status") sind
                            unveränderlich → kein Menü; nur Custom-Spalten. */}
                        {!c.locked && (
                          <button
                            type="button"
                            className="ryfe-th__menu"
                            title="Spalte bearbeiten"
                            aria-label={`Spalte „${c.name}" bearbeiten`}
                            onClick={(e) => {
                              e.stopPropagation();
                              const r = (
                                e.currentTarget as HTMLElement
                              ).getBoundingClientRect();
                              // Menü an den Button verankern und gegen den
                              // Viewport klemmen (x rechts, y unten), damit es bei
                              // keiner Spalte abgeschnitten wird / verschwindet.
                              const x = Math.max(
                                8,
                                Math.min(r.left, window.innerWidth - 184),
                              );
                              const y = Math.min(
                                r.bottom + 2,
                                window.innerHeight - 96,
                              );
                              setColMenu((m) =>
                                m?.id === c.id ? null : { id: c.id, x, y },
                              );
                            }}
                          >
                            ⋮
                          </button>
                        )}
                      </div>
                      <span
                        className="ryfe-th__resize"
                        title="Spaltenbreite ziehen"
                        onMouseDown={(e) => startResize(e, c.id)}
                      />
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {displayRows.map(({ row: r, depth, hasKids }) => (
                  <tr key={r.id} className="ryfe-row">
                    <td className="ryfe-list__sys ryfe-list__seq">
                      {/* R4 — Ausklapp-Pfeil + Einrückung (nur im Tree-Modus).
                          Ein ▶ klappt nach unten (▼) auf; ohne Kinder bleibt der
                          Platz für die Bündigkeit erhalten. */}
                      {isTree && (
                        <span
                          className="ryfe-tree__lead"
                          style={{ paddingLeft: `${(depth - 1) * 1.1}rem` }}
                        >
                          {hasKids ? (
                            <button
                              type="button"
                              className="ryfe-tree__toggle"
                              aria-label={`${
                                treeParentCol
                                  ? "Unterprozesse"
                                  : "Unteraufgaben"
                              } ${
                                expanded.has(r.id) ? "einklappen" : "ausklappen"
                              }`}
                              aria-expanded={expanded.has(r.id)}
                              onClick={() => toggleExpanded(r.id)}
                            >
                              {expanded.has(r.id) ? "▼" : "▶"}
                            </button>
                          ) : (
                            <span className="ryfe-tree__spacer" />
                          )}
                        </span>
                      )}
                      {r.seq ?? "—"}
                    </td>
                    <td className="ryfe-list__sys ryfe-list__uuid" title={r.id}>
                      {r.id}
                    </td>
                    {visibleColumns.map((c) => (
                      <td key={c.id}>{renderCell(r, c)}</td>
                    ))}
                    <td className="ryfe-list__rowactions">
                      {/* R4 — Unteraufgabe (L(n+1)) anlegen, bis 4 Ebenen. Nur
                          für list_rows-Unterwerte; ein Spalten-Baum (Weg 2 A2,
                          z. B. Prozesse) legt keine Kinder inline an. */}
                      {isTree && !treeParentCol && depth < MAX_LEVELS && (
                        <button
                          className="ryfe-btn ryfe-btn--ghost"
                          onClick={() => addSubRow(r.id)}
                          title="Unteraufgabe hinzufügen"
                          aria-label={`Unteraufgabe unter Zeile ${
                            r.seq ?? ""
                          } hinzufügen`}
                        >
                          ＋
                        </button>
                      )}
                      <button
                        className="ryfe-btn ryfe-btn--ghost"
                        onClick={() => setDetailRowId(r.id)}
                        title="Zeile öffnen (Detailansicht)"
                      >
                        ⋯
                      </button>
                      <button
                        className="ryfe-btn ryfe-btn--ghost"
                        onClick={() => removeRow(r)}
                        title="Zeile löschen"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
                {displayRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={visibleColumns.length + 3}
                      className="ryfe-muted"
                    >
                      Keine Zeilen.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* R4 — Listeneinstellungen (shared): „Unterwerte zulassen". */}
      {/* AP1 — „aus Prozess erstellen" (Aufgaben-Liste): Prozess wählen →
          Pflicht-Inputs → materialisieren; die Gruppe wird serverseitig gestempelt. */}
      {instantiateOpen && wsId && (
        <ProcessInstantiateDialog
          wsId={wsId}
          groupId={activeGroupId}
          onClose={() => setInstantiateOpen(false)}
          onDone={() => void load(wsId)}
        />
      )}

      {/* Welle R — Anlage-Formular für gebundene Listen mit Pflichtfeldern
          (Prozesse). Sammelt die `create_fields` und legt dann die Zeile an. */}
      {createDraft && createFields && (
        <div
          className="ryfe-modal"
          role="dialog"
          aria-modal="true"
          onClick={() => setCreateDraft(null)}
        >
          <div
            className="ryfe-modal__card"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="ryfe-panel__title">Neu anlegen</h2>
            {createFields.filter(fieldVisible).map((f) => (
              <label key={f.id} className="ryfe-field">
                <span>
                  {f.label}
                  {f.required && " *"}
                </span>
                {f.type === "select" ? (
                  <select
                    value={createDraft[f.id] || ""}
                    onChange={(e) =>
                      setCreateDraft({ ...createDraft, [f.id]: e.target.value })
                    }
                  >
                    {(f.options || []).map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                ) : f.type === "textarea" ? (
                  <textarea
                    rows={3}
                    value={createDraft[f.id] || ""}
                    onChange={(e) =>
                      setCreateDraft({ ...createDraft, [f.id]: e.target.value })
                    }
                  />
                ) : f.type === "date" ? (
                  <input
                    type="date"
                    value={createDraft[f.id] || ""}
                    onChange={(e) =>
                      setCreateDraft({ ...createDraft, [f.id]: e.target.value })
                    }
                  />
                ) : f.type === "rrule" ? (
                  <RRuleSelector
                    value={createDraft[f.id] || DEFAULT_RRULE}
                    onChange={(v) =>
                      setCreateDraft({ ...createDraft, [f.id]: v })
                    }
                  />
                ) : (
                  <input
                    value={createDraft[f.id] || ""}
                    onChange={(e) =>
                      setCreateDraft({ ...createDraft, [f.id]: e.target.value })
                    }
                  />
                )}
              </label>
            ))}
            <div className="ryfe-modal__actions">
              <button className="ryfe-btn" onClick={() => setCreateDraft(null)}>
                Abbrechen
              </button>
              <span className="ryfe-topbar__spacer" />
              <button
                className="ryfe-btn ryfe-btn--primary"
                onClick={() => void submitCreate()}
              >
                Anlegen
              </button>
            </div>
          </div>
        </div>
      )}

      {listSettingsOpen && (
        <div
          className="ryfe-modal"
          role="dialog"
          aria-modal="true"
          onClick={() => setListSettingsOpen(false)}
        >
          <div
            className="ryfe-modal__card"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="ryfe-panel__title">Listeneinstellungen</h2>
            <label className="ryfe-check">
              <input
                type="checkbox"
                checked={!!snap.settings?.allow_subvalues}
                onChange={(e) => setAllowSubvalues(e.target.checked)}
              />
              <span>
                Unterwerte zulassen (Aufgaben-Hierarchie bis {MAX_LEVELS}{" "}
                Ebenen)
              </span>
            </label>
            <p className="ryfe-muted" style={{ padding: 0 }}>
              Ist dies aktiv, kann jede Zeile über den Pfeil links auf- und
              zugeklappt werden und Unteraufgaben erhalten.
            </p>
            <div className="ryfe-modal__actions">
              <span className="ryfe-topbar__spacer" />
              <button
                className="ryfe-btn ryfe-btn--primary"
                onClick={() => setListSettingsOpen(false)}
              >
                Fertig
              </button>
            </div>
          </div>
        </div>
      )}

      {/* R4 — Ansichtseinstellungen (pro Nutzer): „standardmäßig ausgeklappt". */}
      {viewSettingsOpen && (
        <div
          className="ryfe-modal"
          role="dialog"
          aria-modal="true"
          onClick={() => setViewSettingsOpen(false)}
        >
          <div
            className="ryfe-modal__card"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="ryfe-panel__title">Ansichtseinstellungen</h2>
            <label className="ryfe-check">
              <input
                type="checkbox"
                checked={defaultExpanded}
                onChange={(e) => {
                  const on = e.target.checked;
                  setDefaultExpanded(on);
                  writeExpandDefault(fileId, on);
                  // Sofort anwenden: an ⇒ alles aufklappen, aus ⇒ alles zu.
                  if (on) {
                    const parents = new Set<string>();
                    for (const r of snap.rows) {
                      if (r.parent_row_id) {
                        parents.add(r.parent_row_id);
                      }
                    }
                    setExpanded(parents);
                  } else {
                    setExpanded(new Set());
                  }
                }}
              />
              <span>Alle Elemente standardmäßig ausgeklappt</span>
            </label>
            <p className="ryfe-muted" style={{ padding: 0 }}>
              Gilt für diese Ansicht (nur für dich). „Unterwerte zulassen" liegt
              dagegen in den Listeneinstellungen.
            </p>
            <div className="ryfe-modal__actions">
              <span className="ryfe-topbar__spacer" />
              <button
                className="ryfe-btn ryfe-btn--primary"
                onClick={() => setViewSettingsOpen(false)}
              >
                Fertig
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spalten-Menü als EIN Root-Overlay (position:fixed) — nicht mehr im
          jeweiligen <th>. So kann keine Tabellen-/Overflow-Clip-Grenze es
          abschneiden; es öffnete sonst nur bei der untersten Spalte sauber. */}
      {colMenu &&
        (() => {
          const c = snap.columns.find((col) => col.id === colMenu.id);
          if (!c) {
            return null;
          }
          return (
            <ul
              className="ryfe-context ryfe-context--col"
              style={{ top: colMenu.y, left: colMenu.x }}
              onClick={(e) => e.stopPropagation()}
            >
              <li>
                <button
                  onClick={() => {
                    setColDialog({ col: c, position: c.position });
                    setColMenu(null);
                  }}
                >
                  Einstellungen…
                </button>
              </li>
              {/* AP-6 (P7.2) — Backfill nur für Auto-Create-Spalten. */}
              {c.type === "relation" &&
                c.config?.mode === "board" &&
                Boolean(c.config?.auto_create) && (
                  <li>
                    <button
                      onClick={() => {
                        void backfillColumn(c);
                        setColMenu(null);
                      }}
                    >
                      Auto-Dateien nachlegen
                    </button>
                  </li>
                )}
              <li>
                <button
                  className="ryfe-context__danger"
                  onClick={() => {
                    removeColumn(c);
                    setColMenu(null);
                  }}
                >
                  Spalte löschen
                </button>
              </li>
            </ul>
          );
        })()}

      {colDialog && wsId && (
        <ColumnDialog
          wsId={wsId}
          currentFileId={fileId}
          initial={colDialog.col}
          onClose={() => setColDialog(null)}
          onSubmit={submitColumn}
        />
      )}

      {/* CC_11-Revision — the forms manager over the D-Forms backend. */}
      {formsOpen && wsId && (
        <FormsDialog
          wsId={wsId}
          fileId={fileId}
          columns={snap.columns}
          onClose={() => setFormsOpen(false)}
        />
      )}

      {shareOpen && (
        <ShareSubmenu
          target={{ kind: "file", id: fileId, name }}
          wsId={wsId ?? undefined}
          onClose={() => setShareOpen(false)}
          onInviteByEmail={
            wsId
              ? async (email, role) => {
                  await ryfeApi.inviteToFile(
                    wsId,
                    fileId,
                    email,
                    role === "editor" ? "editor" : "viewer",
                  );
                }
              : undefined
          }
          onCreateSnapshot={
            wsId
              ? () =>
                  createEncryptedSnapshot({
                    workspaceId: wsId,
                    fileId,
                    snapshot: {
                      kind: "list",
                      name,
                      columns: snap.columns.map((c) => ({
                        id: c.id,
                        name: c.name,
                      })),
                      rows: snap.rows.map((r) => ({
                        id: r.id,
                        seq: r.seq,
                        data: r.data,
                      })),
                    },
                  })
              : undefined
          }
        />
      )}

      {/* CC_11 Block 1 (D-69) — the ONE relation picker (row/detail view). */}
      {relPicker &&
        wsId &&
        (() => {
          const col = snap.columns.find((c) => c.id === relPicker.colId);
          const row = snap.rows.find((r) => r.id === relPicker.rowId);
          if (!col || !row) {
            return null;
          }
          return (
            <RelationPicker
              wsId={wsId}
              fileId={fileId}
              column={col}
              current={row.links?.[col.id] || []}
              onClose={() => setRelPicker(null)}
              onSave={async (ids) => {
                await ryfeApi.updateListRow(wsId, fileId, row.id, {
                  links: { [col.id]: ids },
                });
                await load(wsId);
                setRelPicker(null);
              }}
            />
          );
        })()}

      {/* Zeilen-Detailview (Mockup v3 D) — alle Spalten typgerecht editierbar,
          System-Felder read-only. CC_11 B3 (D-71): EIN Kommentar-Thread je Zeile. */}
      {(() => {
        if (!detailRowId) {
          return null;
        }
        const dr = snap.rows.find((r) => r.id === detailRowId);
        if (!dr) {
          return null;
        }
        const titleCol = snap.columns.find((c) => c.type === "text");
        const titleRaw = titleCol ? dr.data[titleCol.id] : undefined;
        const title =
          titleRaw !== undefined && titleRaw !== null && String(titleRaw).trim()
            ? String(titleRaw)
            : `Zeile #${dr.seq ?? ""}`;
        return (
          <div
            className="ryfe-modal"
            role="dialog"
            aria-modal="true"
            onClick={() => setDetailRowId(null)}
          >
            <div
              className="ryfe-modal__card ryfe-modal__card--detail"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ryfe-detail__head">
                <h2 className="ryfe-panel__title">{title}</h2>
                <button
                  className="ryfe-btn ryfe-btn--ghost"
                  onClick={() => setDetailRowId(null)}
                >
                  ×
                </button>
              </div>
              <div className="ryfe-detail__body">
                <div className="ryfe-detail__row">
                  <span className="ryfe-detail__k">row_id</span>
                  <span className="ryfe-muted">{dr.seq ?? "—"}</span>
                </div>
                <div className="ryfe-detail__row">
                  <span className="ryfe-detail__k">UUID</span>
                  <span className="ryfe-muted ryfe-list__uuid">{dr.id}</span>
                </div>
                {snap.columns.map((c) => (
                  <div key={c.id} className="ryfe-detail__row">
                    <span className="ryfe-detail__k">
                      {c.name} · {TYPE_LABELS[c.type]}
                    </span>
                    <span className="ryfe-detail__v">
                      {renderCell(dr, c, "detail")}
                    </span>
                  </div>
                ))}
                {/* A3c (D-158) — Adress-Subgrid: nur bei einer gebundenen
                    Kontakte-Liste; die Zeilen-ID ist die contact_id. */}
                {wsId && snap.settings?.detail_addresses && (
                  <ContactAddresses wsId={wsId} contactId={dr.id} />
                )}
                {/* CC_11 Block 3 (D-71) — ONE comment thread per record. */}
                {wsId && (
                  <RowComments wsId={wsId} fileId={fileId} rowId={dr.id} />
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};
